import React, { useState } from 'react'
import {
  MagnifyingGlassIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline'
import { useListDevHandoffsQuery } from '../store/api/devHandoffsApi'
import { formatDateTimeEST } from '../utils/dateUtils'

const WEB_BASE_URL = (import.meta as any).env?.VITE_WEB_URL
  || (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : 'https://web.motionode.com')

function StatusBadge({ status }: { status: string }) {
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-50 text-green-700 border border-green-200">
        ✓ Sent
      </span>
    )
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-50 text-red-700 border border-red-200">
        ✗ Failed
      </span>
    )
  }
  return (
    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
      {status}
    </span>
  )
}

const DevHandoffs: React.FC = () => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [copiedToken, setCopiedToken] = useState<string | null>(null)

  const { data, isLoading, error } = useListDevHandoffsQuery({
    page,
    limit: 50,
    search: search || undefined,
  })

  const handleCopyLink = (token: string) => {
    const url = `${WEB_BASE_URL}/dev-handoff/${token}`
    navigator.clipboard.writeText(url).then(() => {
      setCopiedToken(token)
      setTimeout(() => setCopiedToken(null), 2000)
    })
  }

  const rows = data?.data ?? []
  const meta = data?.meta

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading developer handoffs. Please try again.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Developer Handoffs</h1>
        <p className="text-gray-600">
          Markdown task files sent to developers via the Developer Handoff feature
        </p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        {/* Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by developer name, email or project…"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1)
                }}
              />
            </div>
          </div>
        </div>

        {/* Stats strip */}
        {meta && (
          <p className="text-xs text-gray-500 mb-4">
            {meta.total} handoff{meta.total !== 1 ? 's' : ''} total
          </p>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Developer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">View link</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                    No developer handoffs found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const handoffUrl = `${WEB_BASE_URL}/dev-handoff/${row.token}`
                  return (
                    <tr key={row.id} className="hover:bg-gray-50">
                      {/* Developer */}
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {row.member_name || <span className="text-gray-400 italic">no name</span>}
                        </div>
                        {row.member_email && <div className="text-xs text-gray-500">{row.member_email}</div>}
                      </td>

                      {/* Project */}
                      <td className="px-4 py-4 text-sm text-gray-900 max-w-xs truncate" title={row.project_name ?? undefined}>
                        {row.project_name || '—'}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <StatusBadge status={row.status} />
                      </td>

                      {/* Sent at */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateTimeEST(row.created_at)}
                      </td>

                      {/* View link */}
                      <td className="px-4 py-4">
                        <span className="text-xs text-gray-400 font-mono break-all">
                          {handoffUrl}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleCopyLink(row.token)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                            title="Copy handoff link"
                          >
                            {copiedToken === row.token ? (
                              <><CheckIcon className="h-3.5 w-3.5" />Copied</>
                            ) : (
                              <><ClipboardDocumentIcon className="h-3.5 w-3.5" />Copy link</>
                            )}
                          </button>
                          <a
                            href={handoffUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                            title="Open handoff"
                          >
                            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                            Open
                          </a>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta && meta.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-700">
              Showing {(meta.page - 1) * meta.limit + 1}–
              {Math.min(meta.page * meta.limit, meta.total)} of {meta.total}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={meta.page <= 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                disabled={meta.page >= meta.totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DevHandoffs
